import React, { useEffect, useRef, useState } from "react";
import { DevRequestDetail, DevRequestStatus as S } from "../types";
import { devRequestService } from "../services/dataService";
import { DR_STATUS_LABEL, drErr, drTransitionLabel } from "../utils/devRequests";
import "../pages/PurchaseOrders.css";
import "../pages/DevRequests.css";

/**
 * Talep sahibinin durum işlemi (ShipmentActionModal düzeni): geri çekme ya da 30 gün içinde yeniden açma.
 * Diğer durum geçişleri ve hedef tarih yalnız Nedaris Destek'te; panelde yöneticiye işlem penceresi yok.
 * İstek her zaman expectedStatus taşır: talep bu arada değiştiyse sunucu 409 döner, modal kapanır ve sayfa kartı yeniler.
 */
export interface DevRequestActionModalProps {
  request: DevRequestDetail;
  /** Geri çek (Withdrawn) ya da yeniden aç (Tamamlandı → InReview). */
  to: S.Withdrawn | S.InReview;
  onClose: () => void;
  /** isError: 409/404 — sayfa metni bildirir, listeyi ve kartı yeniler. */
  onDone: (msg: string, isError?: boolean) => void;
}

interface NoteSpec { label: string; min: number; err?: string; hint?: string }
/** Hedefe göre not alanı (§10.9). min > 0 = zorunlu; err sunucunun aynı metni. */
const noteSpec = (to: S): NoteSpec => (to === S.InReview
  ? { label: "Neden yeniden açıyorsunuz?", min: 10, err: "Neyin hâlâ çözülmediğini yazın (en az 10 karakter).", hint: "Talep yeniden incelemeye alınır." }
  : { label: "Sebep", min: 0, hint: "Geri çekilen talep kapanır; gerekirse Nedaris Destek yeniden açabilir." });
const doneMessage = (to: S): string => (to === S.InReview ? "Talep yeniden açıldı." : "Talep geri çekildi.");
// po-* yalnız input.is-invalid tanımlar; not kutusu aynı kırmızı çerçeveyi buradan alır
const INVALID: React.CSSProperties = { borderColor: "var(--po-danger)", boxShadow: "0 0 0 3px rgba(180,35,24,.12)" };

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
};

const DevRequestActionModal: React.FC<DevRequestActionModalProps> = ({ request, to, onClose, onDone }) => {
  const from = request.status;
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  // Açan öğe ilk render'da (odak modala geçmeden) yakalanır
  const [opener] = useState(() => document.activeElement as HTMLElement | null);

  const spec = noteSpec(to);
  const noteErr = spec.min > 0 && note.trim().length < spec.min ? spec.err || "" : "";
  const danger = to === S.Withdrawn;
  const label = drTransitionLabel(from, to);

  const run = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    setError(""); setTried(true);
    if (noteErr) { noteRef.current?.focus(); return; }
    setSaving(true);
    try {
      await devRequestService.changeStatus(request.devRequestId, { to, expectedStatus: from, note: note.trim() || null });
      onDone(doneMessage(to));
      onClose();
    } catch (err: any) {
      const st = err?.response?.status;
      // Talep bu arada değişti ya da silindi: modal kapanır, sayfa sunucu metnini gösterip kartı yeniler
      if (st === 409 || st === 404) { onDone(drErr(err, "Talep bu arada güncellendi; kart yenilendi."), true); onClose(); return; }
      setError(drErr(err, "İşlem yapılamadı."));
    } finally { setSaving(false); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // stopPropagation şart: arkadaki çekmece aynı Esc ile kapanmasın
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (!saving) onClose(); }
      else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); run(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  // autoFocus yerine efekt: StrictMode geliştirmede geri-odak temizliğini bir kez erken çalıştırır, autoFocus ezilirdi
  useEffect(() => { noteRef.current?.focus(); }, []);

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="dr-act-title" noValidate onSubmit={run}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">{request.number} · {DR_STATUS_LABEL[from]}</span>
            <h3 id="dr-act-title">{label}</h3>
            <p className="po-form-hint">{request.title}</p>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          {error && <div className="po-note err" role="alert">{error}</div>}
          <label className="po-field">
            <span>{spec.label}<em>{spec.min > 0 ? `zorunlu, en az ${spec.min} karakter` : "isteğe bağlı"}</em></span>
            <textarea ref={noteRef} rows={spec.min > 0 ? 4 : 3} maxLength={2000} value={note} style={tried && noteErr ? INVALID : undefined} aria-invalid={tried && !!noteErr}
              onChange={(e) => { setNote(e.target.value); setError(""); }} />
            <span className="po-counter">{note.length}/2000</span>
            {tried && noteErr && <span className="po-fielderr">{noteErr}</span>}
          </label>
          {spec.hint && <div className="po-note info">{spec.hint}</div>}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint"><b>{DR_STATUS_LABEL[from]}</b> → <b>{DR_STATUS_LABEL[to]}</b></span>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="submit" className={`po-btn primary${danger ? " danger" : ""}`} disabled={saving}>
              {saving ? "Kaydediliyor…" : label}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DevRequestActionModal;
